import { Separator } from '@/components/ui/separator'
import classNames from 'classnames/bind'
import { Fragment, useEffect, useRef } from 'react'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import * as styles from './style'
import ArticleCardRow from '@/compositions/V2/ArticleCardRow'
import HelenResponseSessionCardRow from '@/compositions/V2/HelenResponseSessionCardRow'
import { ClassV2Session } from '@/shared/api-generated-types'

const cx = classNames.bind(styles)

export interface ChatMessage {
  type: 'USER' | 'BOT_TYPING'
  content: string
  hidden: boolean
  id: string
  name: string
  timestamp: number
  userId: string
  metadata?: Record<string, any>
}

export interface CLassMetaData {
  id: string
  title: string
  slug: string
  imageUrl?: string
  maxWidthImageUrl?: string
  durationSeconds?: number
  guideId?: string
  guideName?: string
  guideAvatarUrl?: string
  aiSessions: ClassV2Session[]
  next_session: string | null
  watch_url: string
  class_detail_url: string
  hasVOD?: boolean
  hasZoom?: boolean
  hasSessions?: boolean
}

export interface ArticleMetaData {
  id: string
  title: string
  slug: string
  imageUrl?: string
  maxWidthImageUrl?: string
  guideId?: string
  guideName?: string
  guideAvatarUrl?: string
  readTimeInSeconds?: number
  publishedAt?: string
  body?: string
}

interface Props {
  message: ChatMessage
  showDivider: boolean
  isLastMessage: boolean
  isHelenSandbox?: boolean
}

export const MarkdownRenderer = ({ children }: { children: string }): JSX.Element => (
  <Markdown
    rehypePlugins={[rehypeRaw]}
    remarkPlugins={[remarkGfm]}
    components={{
      a: ({ node, ...props }) => <a className="underline text-secondary" {...props} />,
    }}
  >
    {children}
  </Markdown>
)

const Message = (props: Props): JSX.Element => {
  const elementRef = useRef<HTMLDivElement>(null)

  const botName = props.isHelenSandbox ? 'Sandy' : 'Helen'
  const sandboxColorClass = props.isHelenSandbox ? 'text-red-600' : ''

  useEffect(() => {
    if (!elementRef.current || !props.isLastMessage) return
    elementRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [props.isLastMessage])

  const classes = cx({
    aiMessage: props.message.userId === 'ai-response',
    userMessage: props.message.userId !== 'ai-response',
  })

  type TagType = 'CLASSES' | 'ARTICLES'
  type TagMatch = { type: TagType; ids: string[] }

  const extractAllTags = (content: string): TagMatch[] => {
    const regex = /<(CLASSES|ARTICLES) LIST="([^"]+)"\/?>/g
    const matches: TagMatch[] = []
    let match
    while ((match = regex.exec(content)) !== null) {
      matches.push({ type: match[1] as TagType, ids: match[2].split(',') })
    }
    return matches
  }

  // Split content on both tags
  const splitContent = (content: string): string[] => {
    return content.split(/<(?:CLASSES|ARTICLES) LIST="[^"]+"\/?>/g).filter(Boolean)
  }

  const allTags = extractAllTags(props.message.content)
  const contentParts = splitContent(props.message.content)

  // Build maps for classes and articles
  const classMap: Record<string, CLassMetaData> = {}
  const articleMap: Record<string, ArticleMetaData> = {}

  props.message.metadata?.toolResults?.forEach((meta: any) => {
    if (meta.function_name === 'get_relevant_classes' && Array.isArray(meta.output)) {
      meta.output.forEach((item: CLassMetaData) => {
        classMap[item.id] = item
      })
    } else if (meta.function_name === 'get_helpdesk_articles' && Array.isArray(meta.output)) {
      meta.output.forEach((item: ArticleMetaData) => {
        articleMap[item.id] = item
      })
    }
  })

  return props.message.type === 'BOT_TYPING' ? (
    <div className="mt-4 text-lg bg-[#f1f1ff] p-3 font-semibold rounded-md flex gap-5 items-center">
      <div className={`self-end ${sandboxColorClass}`}>{botName} is thinking</div>
      <div className={styles.dotTyping}></div>
    </div>
  ) : (
    <div>
      {props.message.userId === 'ai-response' && (
        <div className={`${styles.aiHeader} ${sandboxColorClass} p-3 pl-0`}>Answer from {botName}</div>
      )}
      <div>
        <div
          ref={elementRef}
          className={`${
            props.message.userId === 'ai-response'
              ? 'bg-[#F7F7FA] p-3 lg:p-[18px] xxl:py-6 xxl:px-[18px] xxxl:p-6 rounded-2xl'
              : ''
          }`}
        >
          <div className={classes}>
            {contentParts.map((part, idx) => (
              <Fragment key={idx}>
                {part && <MarkdownRenderer>{part}</MarkdownRenderer>}
                {allTags[idx] && allTags[idx].type === 'CLASSES' && allTags[idx].ids.length > 0 && (
                  <div className="w-full text-sm text-gray-700">
                    <HelenResponseSessionCardRow
                      className={`${styles.cardRow}`}
                      items={allTags[idx].ids.map((id) => classMap[id]).filter(Boolean)}
                    />
                  </div>
                )}
                {allTags[idx] && allTags[idx].type === 'ARTICLES' && allTags[idx].ids.length > 0 && (
                  <div className="w-full text-sm text-gray-700">
                    <ArticleCardRow
                      className={`${styles.cardRow}`}
                      items={allTags[idx].ids.map((id) => articleMap[id]).filter(Boolean)}
                    />
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </div>
        {props.message.userId === 'ai-response' && props.showDivider && <Separator className="my-4" />}
      </div>
    </div>
  )
}

export default Message
